import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type {
  AlbTargetGroupState,
  CloudWatchAlarmState,
  EcsServiceState,
  RdsInstanceState,
  StaffTelemetry,
} from './types.js';
import { relativeTime } from './format.js';

function shortArn(arn: string): string {
  return arn.split('/').slice(-2).join('/') || arn;
}

function alarmColor(state: CloudWatchAlarmState['state']): 'success' | 'error' | 'default' {
  if (state === 'OK') return 'success';
  if (state === 'ALARM') return 'error';
  return 'default';
}

function ecsColor(svc: EcsServiceState): 'success' | 'warning' | 'error' {
  if (svc.running === 0) return 'error';
  if (svc.running < svc.desired) return 'warning';
  return 'success';
}

function rdsColor(state: string): 'success' | 'warning' | 'error' {
  if (state === 'available') return 'success';
  if (state === 'failed' || state === 'incompatible-network' || state === 'incompatible-restore') return 'error';
  return 'warning';
}

function targetColor(tg: AlbTargetGroupState): 'success' | 'warning' | 'error' {
  if (tg.healthy === 0) return 'error';
  if (tg.unhealthy > 0) return 'warning';
  return 'success';
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h3" component="h3" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function EcsTable({ rows }: { rows: EcsServiceState[] }) {
  if (rows.length === 0) return <Typography variant="body2" color="text.secondary">No services reporting.</Typography>;
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Cluster</TableCell>
            <TableCell>Service</TableCell>
            <TableCell align="right">Running / desired</TableCell>
            <TableCell align="right">Deployments</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.cluster}-${r.service}`}>
              <TableCell><Typography variant="caption" color="text.secondary">{r.cluster}</Typography></TableCell>
              <TableCell>{r.service}</TableCell>
              <TableCell align="right">{r.running} / {r.desired}</TableCell>
              <TableCell align="right">{r.deployments}</TableCell>
              <TableCell><Chip size="small" color={ecsColor(r)} label={r.running >= r.desired ? 'healthy' : 'degraded'} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RdsTable({ rows }: { rows: RdsInstanceState[] }) {
  if (rows.length === 0) return <Typography variant="body2" color="text.secondary">No instances reporting.</Typography>;
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Identifier</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Multi-AZ</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.identifier}>
              <TableCell>{r.identifier}</TableCell>
              <TableCell><Chip size="small" color={rdsColor(r.status)} label={r.status} /></TableCell>
              <TableCell>{r.multi_az ? 'yes' : 'no'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AlarmsTable({ rows }: { rows: CloudWatchAlarmState[] }) {
  if (rows.length === 0) return <Typography variant="body2" color="text.secondary">No alarms reporting.</Typography>;
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Namespace</TableCell>
            <TableCell>Metric</TableCell>
            <TableCell align="right">State</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.name}>
              <TableCell><Typography variant="body2">{r.name}</Typography></TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">{r.namespace}</Typography></TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">{r.metric}</Typography></TableCell>
              <TableCell align="right"><Chip size="small" color={alarmColor(r.state)} label={r.state} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AlbTable({ rows }: { rows: AlbTargetGroupState[] }) {
  if (rows.length === 0) return <Typography variant="body2" color="text.secondary">No target groups reporting.</Typography>;
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Target group</TableCell>
            <TableCell align="right">Healthy</TableCell>
            <TableCell align="right">Unhealthy</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.arn}>
              <TableCell><Typography variant="caption" color="text.secondary">{shortArn(r.arn)}</Typography></TableCell>
              <TableCell align="right">{r.healthy}</TableCell>
              <TableCell align="right">{r.unhealthy}</TableCell>
              <TableCell><Chip size="small" color={targetColor(r)} label={r.healthy === 0 ? 'down' : r.unhealthy > 0 ? 'mixed' : 'healthy'} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Office-IP-gated telemetry panel. The fetch lives in App.tsx so the
 * Telemetry tab only appears when the WAF allows the viewer through;
 * non-office viewers never see the tab or a hint of one. This component
 * is purely presentational — it receives a non-null telemetry payload
 * and renders the four AWS tables.
 */
export function StaffPanel({ telemetry }: { telemetry: StaffTelemetry }) {
  return (
    <Box component="section">
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Generated {relativeTime(telemetry.generated_at)}
      </Typography>

      <Section title="ECS services">
        <EcsTable rows={telemetry.ecs_services} />
      </Section>

      <Section title="RDS instances">
        <RdsTable rows={telemetry.rds_instances} />
      </Section>

      <Section title="CloudWatch alarms">
        <AlarmsTable rows={telemetry.cloudwatch_alarms} />
      </Section>

      <Section title="ALB target groups">
        <AlbTable rows={telemetry.alb_target_groups} />
      </Section>
    </Box>
  );
}
